import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { usePublicPageSection } from "../../../../hooks/usePublicPageSection";
import { useLanguage } from "../../../../context/LanguageContext";
import "./CaseStatus.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(
      `Case status field "${key}" is missing from the API response.`
    );
  }
  return value;
};

const buildModel = (section) => {
  const f = section.fields || {};
  return {
    badge: requireField(f, "badge"),
    copyLabel: requireField(f, "copyLabel"),
    copiedLabel: requireField(f, "copiedLabel"),
    noteStrong: requireField(f, "noteStrong"),
    noteText: requireField(f, "noteText"),
    nextTitle: requireField(f, "nextTitle"),
    nextSteps: [1, 2, 3].map((n) => ({
      step: requireField(f, `step${n}Number`),
      title: requireField(f, `step${n}Title`),
      text: requireField(f, `step${n}Text`),
    })),
    trackButton: requireField(f, "trackButton"),
    trackUrl: requireField(f, "trackUrl"),
    homeButton: requireField(f, "homeButton"),
    homeUrl: requireField(f, "homeUrl"),
  };
};

const CaseStatus = () => {
  const location = useLocation();
  const caseId = location.state?.caseId || "";
  const { meta } = useLanguage();
  const { model, error: fetchError, isLoading } = usePublicPageSection(
    "report-submitted",
    "case-status",
    buildModel
  );
  const [copied, setCopied] = useState(false);

  const caseIdError = !caseId
    ? "No Case ID was provided. Submit a report to receive one."
    : "";
  const error = caseIdError || fetchError;
  const loading = caseId && isLoading;

  const handleCopy = async () => {
    if (!caseId) return;
    try {
      await navigator.clipboard.writeText(caseId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (loading && !model) {
    return (
      <section className="case-status" dir={meta.dir}>
        <div className="case-status__container">
          <p style={{ color: "#64748b", fontSize: 14 }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (error || !model) {
    return (
      <section className="case-status" dir={meta.dir}>
        <div className="case-status__container">
          <p style={{ color: "#b91c1c", fontSize: 14 }}>
            {error || "Case status unavailable."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="case-status" dir={meta.dir}>
      <div className="case-status__container">
        <div className="case-status__card">
          <span className="case-status__badge">{model.badge}</span>

          <div className="case-status__id-row">
            <p className="case-status__id">{caseId}</p>
            <button
              type="button"
              className="case-status__copy"
              onClick={handleCopy}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4.16669 12.4998H3.33335C2.89133 12.4998 2.4674 12.3242 2.15484 12.0117C1.84228 11.6991 1.66669 11.2752 1.66669 10.8332V3.33317C1.66669 2.89114 1.84228 2.46722 2.15484 2.15466C2.4674 1.8421 2.89133 1.6665 3.33335 1.6665H10.8334C11.2754 1.6665 11.6993 1.8421 12.0119 2.15466C12.3244 2.46722 12.5 2.89114 12.5 3.33317V4.1665M9.16669 7.49984H16.6667C17.5872 7.49984 18.3334 8.24603 18.3334 9.1665V16.6665C18.3334 17.587 17.5872 18.3332 16.6667 18.3332H9.16669C8.24621 18.3332 7.50002 17.587 7.50002 16.6665V9.1665C7.50002 8.24603 8.24621 7.49984 9.16669 7.49984Z"
                  stroke="currentColor"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{copied ? model.copiedLabel : model.copyLabel}</span>
            </button>
          </div>

          <hr className="case-status__divider" />

          <p className="case-status__note">
            <svg
              className="case-status__info-icon"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M10 18.3333C14.6024 18.3333 18.3334 14.6023 18.3334 9.99996C18.3334 5.39759 14.6024 1.66663 10 1.66663C5.39765 1.66663 1.66669 5.39759 1.66669 9.99996C1.66669 14.6023 5.39765 18.3333 10 18.3333Z"
                stroke="#717680"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 13.3333V10"
                stroke="#717680"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 6.66663H10.0083"
                stroke="#717680"
                strokeWidth="1.67"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>
              <strong>{model.noteStrong}</strong> {model.noteText}
            </span>
          </p>
        </div>

        <div className="case-status__next">
          <h2 className="case-status__next-title">{model.nextTitle}</h2>

          <ol className="case-status__timeline">
            {model.nextSteps.map((item) => (
              <li key={item.step} className="case-status__timeline-item">
                <span className="case-status__timeline-number">{item.step}</span>
                <div className="case-status__timeline-copy">
                  <p className="case-status__timeline-title">{item.title}</p>
                  <p className="case-status__timeline-text">{item.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="case-status__actions">
            <Link
              to={model.trackUrl}
              className="case-status__btn case-status__btn--primary"
            >
              <HiMagnifyingGlass aria-hidden="true" />
              <span>{model.trackButton}</span>
            </Link>
            <Link
              to={model.homeUrl}
              className="case-status__btn case-status__btn--secondary"
            >
              {model.homeButton}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStatus;
